import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  addDays, isAfter, isBefore, startOfWeek,
} from 'date-fns';
import { concat } from 'lodash';
import { useMutation } from 'react-query';
import dayId from '../../../../utils/tools/dayId';
import batchEditionCommon from '../../../../validations/stocks/batchEditionSchema';
import batchEditionByType from '../../../../validations/stocks/batchEditionByType';
import useModal from '../../../../utils/hooks/useModal';
import cn from '../../../../utils/tools/cn';

// Services
import useInventoryService from '../../../../services/inventoryService';

// Components
import TabsToggle from '../../../molecules/TabsToggle/TabsToggle';
import SelectField from '../../../atoms/SelectField/SelectField';
import DatePickerInput from '../../../atoms/DatePickerInput/DatePickerInput';
import InputField from '../../../atoms/InputField/InputField';
import Button from '../../../atoms/Button/Button';
import FlashMessage from '../../../molecules/FlashMessage/FlashMessage';
import ProgressBar from '../../../atoms/ProgressBar/ProgressBar';

import styles from './BatchEdition.module.scss';

function BatchEdition({
  products,
  inventoryType,
  productType,
  postStatusMutateAsync,
  putQuantityMutateAsync,
  putYieldMutateAsync,
  isYielding,
}) {
  const { t } = useTranslation();
  const endDatepickerRef = useRef();

  const { closeModal } = useModal();

  const { getInventories } = useInventoryService();

  const [isLoadingChanges, setIsLoadingChanges] = useState(false);
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressOnGoing, setProgressOnGoing] = useState(0);

  const tabs = [
    { value: 'status', label: t('batch.status') },
    { value: 'quantity', label: t('batch.quantity') },
  ];

  const status = [
    { value: true, label: t('batch.active') },
    { value: false, label: t('batch.inactive') },
  ];

  const [type, setType] = useState(tabs[0]);

  const [failedProductIds, setFailedProductIds] = useState([]);
  const [isUpdatedSuccessfully, setIsUpdatedSuccessfully] = useState(false);

  const resolveAllChanges = async (items, key, value, asyncUpdate) => {
    let countFailed = 0;
    setIsLoadingChanges(true);
    setProgressTotal(items?.length);
    setProgressOnGoing(0);

    await items.reduce(async (promise, inventory) => {
      await promise;
      try {
        await asyncUpdate({
          inventoryId: inventory.id,
          [key]: value,
          productId: inventory.productId,
          dayId: inventory.date,
          prevValue: inventory.isActive,
        });
      } catch {
        setFailedProductIds((prev) => concat(prev, [inventory.productId]));
        countFailed += 1;
      }

      setProgressOnGoing((state) => state + 1);
    }, Promise.resolve());

    if (countFailed < items?.length) setIsUpdatedSuccessfully(true);
    setIsLoadingChanges(false);
  };

  const { isLoading: isLoadingInventories, mutate: mutateInventoriesBatch } = useMutation(
    ({ start, end }) => getInventories({
      type: inventoryType,
      start,
      end,
    }),
    {
      onSuccess: (res, { values }) => {
        setFailedProductIds([]);
        setIsUpdatedSuccessfully(false);

        if (res?.data?.inventories) {
          const { inventories } = res.data;

          const items = inventories.filter(
            (inventory) => values.products.some((product) => product.value === inventory.productId),
          );

          if (items?.length) {
            if (isYielding) {
              resolveAllChanges(
                items,
                'yielding',
                values.yieldIsNegative ? (Math.abs(values.yielding) * -1) : values.yielding,
                putYieldMutateAsync,
              );
            } else {
              if (type?.value === 'status') {
                resolveAllChanges(items, 'isActive', values.isActive.value, postStatusMutateAsync);
              }

              if (type?.value === 'quantity') {
                resolveAllChanges(items, 'quantity', values.quantity, putQuantityMutateAsync);
              }
            }
          }
        }
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      products: null,
      start: null,
      end: null,
      isActive: null,
      quantity: '',
      yielding: '',
      yieldIsNegative: false,
    },
    onSubmit: (values) => {
      mutateInventoriesBatch({
        values,
        start: dayId.getDayIdFromDate(values.start),
        end: dayId.getDayIdFromDate(values.end),
      });
    },
    validationSchema: (
      isYielding ? (
        batchEditionCommon.concat(batchEditionByType.yielding)
      ) : (
        type?.value ? batchEditionCommon.concat(batchEditionByType[type.value]) : batchEditionCommon
      )
    ),
    validateOnBlur: true,
  });

  return (
    <div className="p-b-10">
      {!isYielding ? (
        <TabsToggle
          tabs={tabs}
          setActive={(tab) => setType(tab)}
          active={type}
          className="mobile-w-100"
          disabled={isLoadingChanges}
        />
      ) : null}

      <div className={!isYielding ? 'm-t-20' : ''}>
        <SelectField
          id="products"
          name="products"
          options={products.map((product) => ({ label: product.name, value: product.id }))}
          label={t(`batch.${productType}`)}
          error={formik.errors?.products && formik.touched?.products ? formik.errors.products : null}
          setFieldTouched={formik.setFieldTouched}
          setFieldValue={formik.setFieldValue}
          value={formik.values.products}
          isMulti
          clearable={false}
          disabled={isLoadingChanges}
        />
      </div>

      <div className="m-t-10">
        <DatePickerInput
          id="stocksBatchEditionStart"
          label={t('batch.start')}
          date={formik.values.start}
          setDate={(date) => {
            formik.setFieldValue('start', date);
            formik.setFieldValue('end', date);
          }}
          error={formik.errors?.start && formik.touched?.start ? formik.errors.start : null}
          disabled={isLoadingChanges}
          minDate={startOfWeek(new Date())}
          onAfterClose={() => endDatepickerRef?.current?.handleOpenCalendar()}
          showTopLabel
        />
      </div>

      <div className="m-t-10">
        <DatePickerInput
          ref={endDatepickerRef}
          id="stocksBatchEditionEnd"
          label={t('batch.end')}
          date={formik.values.end}
          setDate={(date) => formik.setFieldValue('end', date)}
          disabled={!formik.values.start || isLoadingChanges}
          dateDisabled={(date) => {
            if (formik.values.start) {
              return isBefore(date, formik.values.start) || isAfter(date, addDays(formik.values.start, 30));
            }
            return false;
          }}
          error={formik.errors?.end && formik.touched?.end ? formik.errors.end : null}
          minDate={formik.values.start || startOfWeek(new Date())}
          showTopLabel
        />
      </div>

      {isYielding ? (
        <div className="d-flex a-stretch m-t-10">
          <button
            type="button"
            className={cn([styles.yield_sign, 'd-flex center m-r-10 c-pointer'])}
            onClick={() => formik.setFieldValue('yieldIsNegative', !formik.values.yieldIsNegative)}
            disabled={isLoadingChanges}
          >
            {formik.values.yieldIsNegative ? '-' : '+'}
          </button>

          <div className="flex-auto">
            <InputField
              type="number"
              id="yielding"
              name="yielding"
              value={formik.values.yielding === '' ? '' : Math.round(formik.values.yielding)}
              label={t('batch.amountYield')}
              formik={{
                handleChange: formik.handleChange,
                handleBlur: formik.handleBlur,
                setFieldValue: formik.setFieldValue,
              }}
              error={formik.errors?.yielding && formik.touched?.yielding ? formik.errors.yielding : null}
              maxNumberValue={10000}
              minNumberValue={0}
              disabled={isLoadingChanges}
              step={50}
            />
          </div>
        </div>
      ) : (
        <>
          {type?.value === 'status' ? (
            <div className="m-t-10">
              <SelectField
                id="isActive"
                name="isActive"
                options={status}
                label={t('batch.status')}
                error={formik.errors?.isActive && formik.touched?.isActive ? formik.errors.isActive : null}
                setFieldTouched={formik.setFieldTouched}
                setFieldValue={formik.setFieldValue}
                value={formik.values.isActive}
                disabled={isLoadingChanges}
              />
            </div>
          ) : null}

          {type?.value === 'quantity' ? (
            <div className="m-t-10">
              <InputField
                type="number"
                id="quantity"
                name="quantity"
                value={formik.values.quantity}
                label={t('batch.amount')}
                formik={{
                  handleChange: formik.handleChange,
                  handleBlur: formik.handleBlur,
                  setFieldValue: formik.setFieldValue,
                }}
                error={formik.errors?.quantity && formik.touched?.quantity ? formik.errors.quantity : null}
                maxNumberValue={9000}
                disabled={isLoadingChanges}
              />
            </div>
          ) : null}
        </>
      )}

      {products?.length && failedProductIds?.length ? (
        <div className="m-t-20">
          <FlashMessage
            message={
              t('batch.error', {
                products: products?.filter(
                  (prod) => failedProductIds.some((prodId) => prodId === prod.id),
                ).map((p) => p.name).join(', '),
              })
            }
            color="error"
          />
        </div>
      ) : null}

      {isUpdatedSuccessfully ? (
        <div className="m-t-20">
          <FlashMessage message={isYielding ? t('batch.successYield') : t('batch.success')} color="success" />
        </div>
      ) : null}

      {isLoadingChanges && progressTotal ? (
        <>
          <div className="m-t-20">
            <FlashMessage message={isYielding ? t('batch.pleaseWaitYield') : t('batch.pleaseWait')} />
          </div>

          {progressOnGoing ? (
            <div className="m-t-20">
              <ProgressBar progress={(progressOnGoing * 100) / progressTotal} />
            </div>
          ) : null}
        </>
      ) : null}

      <div className="d-flex a-center j-end m-t-30">
        <Button
          theme="inverted"
          className="m-r-10"
          disabled={isLoadingChanges || isLoadingInventories}
          onClick={() => closeModal()}
        >
          {t('common.close')}
        </Button>

        <Button onClick={formik.submitForm} isLoading={isLoadingChanges || isLoadingInventories}>
          {t('batch.submit')}
        </Button>
      </div>
    </div>
  );
}

BatchEdition.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
  productType: PropTypes.string.isRequired,
  inventoryType: PropTypes.string.isRequired,
  postStatusMutateAsync: PropTypes.func,
  putQuantityMutateAsync: PropTypes.func,
  putYieldMutateAsync: PropTypes.func,
  isYielding: PropTypes.bool,
};

BatchEdition.defaultProps = {
  products: [],
  postStatusMutateAsync: () => {},
  putQuantityMutateAsync: () => {},
  putYieldMutateAsync: () => {},
  isYielding: false,
};

export default BatchEdition;
